// EstimVehReport.tsx
import React, { useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { GridRenderCellParams } from '@mui/x-data-grid';
import LocalizedGrid, { CustomGridColDef } from '../../localizedGrid/LocalizedGridCook3h';
import dayjs, { Dayjs } from "dayjs";
import { useFetch } from '../../../hooks/useFetch2.ts';
import { Event, reportApi } from './api/api.ts';
import { Group } from '../../../types/group';
import { api as groupApi } from "../../../api/groupApi";
import { api as entityApi } from '../../../api/entityApi';
import { api as plateApi } from "../../../api/plateApi";
import { Subtenant, subtenantApi } from '../../../api/subtenantApi';
import { LocalizedDatePicker } from '../../localizedDatePicker/LocalizedDatePicker.tsx';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n.ts';
import { Entity } from '../../../types/entity.ts';
import ActionButtons from '../../ActionButtons.tsx';
import { EventRecord } from '../../../types/event.ts';
import { Delete, PhotoCamera } from '@mui/icons-material';
import { FormModal } from '../../modal/FormModal.tsx';
import EventForm from '../events/EventForm.tsx';
import { formatDateTime } from '../../../tools/util.ts';
import { useAuthStore } from '../../../store/useAuthStore3.ts';
import { usePresetDialogs } from '../../dialog/presetDialogs.tsx';
import NewEventForm from './NewEventForm.tsx';
import { ActionButtonProp } from '../../ActionButtons2.tsx';

interface TransportRow {
    ts_start: string; // timestamptz приходит в виде ISO-строки
    registration_number: string;
    matched_number: string;
    verified_number: string;
    is_enter: boolean;
    customer_id: number;
    car_owner: string;
    group_id: number;
    entity_id_1: number;
    subtenant_id?: number;
}

export const EstimVehReport: React.FC = () => {
    const { t } = useTranslation();

    const [from, setFrom] = useState<Dayjs | null>(dayjs().startOf("day"));
    const [ period_days, setPeriod_days] = useState<number>(1);
    const { data, setData, error, update, loading, } = useFetch<Event[]>(reportApi.getEstimateVeh, [], );
    const { data: groups, } = useFetch<Group[]>(groupApi.get, [], );
    const { data: subtenants } = useFetch<Subtenant[]>(subtenantApi.get, []);
    const { data: entities = [], loading: entityLoading } = useFetch<Entity[]>(entityApi.get, [], { type: 1 });
    const [selectedEvent, setSelectedEvent] = useState<EventRecord | undefined>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalOpenNew, setIsModalOpenNew] = useState(false);
    const { subtenantId, customerId } = useAuthStore();
    const { showConfirm, showInfo, DialogComponent } = usePresetDialogs();

    const columns: CustomGridColDef[] = [
        // { field: 'id', headerName: 'ID', width: 90 },
        { field: 'ts_start', headerName: 'Момент', width: 100, valueFormatter: (v) => formatDateTime(v), },
        { field: 'registration_number', headerName: 'Реєстраційний номер з камери', width: 180, },
        { field: 'verified_number', headerName: 'Реєстраційний номер', width: 180, },
        { field: 'car_brand', headerName: 'Марка', width: 180, },
        { field: 'car_owner', headerName: 'Власник', width: 180, },
        { field: "group_id", headerName: t('PlatesList.group'), type: "singleSelect", flex: 2, width: 100, display: 'flex',
            valueOptions: groups.map( group => ({value: group.group_id, label: group.name})),
            editable: false,
            renderCell: (params: GridRenderCellParams<Group>) => {
                return (
                    <Typography>
                        {groups.find(g => g.group_id === params.row.group_id)?.name}
                    </Typography>
                );
            }
        },  
        { field: "entity_id_1", headerName: t('PlatesList.location'), type: "singleSelect", flex: 2, width: 100, display: 'flex',
            valueOptions: entities.map( e => ({value: e.entity_id, label: e.name})),
            editable: false,
            renderCell: (params: GridRenderCellParams<TransportRow>) => {
                return (
                    <Typography>
                        {entities.find(e => e.entity_id === params.row.entity_id_1)?.name}
                    </Typography>
                );
            }
        },
        { field: "subtenant_id", headerName: t('PlatesList.subtenant'), type: "singleSelect", flex: 2, width: 100, display: 'flex',
            valueOptions: subtenants.map( subtenant => ({value: subtenant.subtenant_id, label: subtenant.name})),
            editable: false,
            // hidden: onlyOwn || !!subtenantId,
            hidden: !!subtenantId,
            renderCell: (params: GridRenderCellParams<Subtenant>) => {
                return (
                    <Typography>
                        {subtenants.find(s => s.subtenant_id === params.row.subtenant_id)?.name}
                    </Typography>
                );
            }
        },
        { field: 'tag', headerName: 'Примітка', width: 180, },
        // { field: 'customer_id', headerName: 'customer_id', width: 180, },
        {
            field: 'actions',
            headerName: t('EventList.actions'),
            width: 150,
            sortable: false,
            renderCell: (params: GridRenderCellParams<EventRecord>) => (
                <ActionButtons actions={[
                    ...(!subtenantId ? [
                    { type: 'delete', action: () => handleDelete(params.row), icon: Delete }
                    ] : []),
                    { type: 'extended', action: () => handleOpenModal(params.row), icon: PhotoCamera }
                ] as ActionButtonProp[]} /> 
            ),
        },        
    ];

    const handleRefresh = () => {
        setData([]);
        update({ moment: from?.toISOString(), period_days });
    };

    const handleOpenModal = (row: EventRecord) => {
        setSelectedEvent(row);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => setIsModalOpen(false);

    const handleOpenNewModal = () => {
        setIsModalOpenNew(true);
    };

    const handleCloseNewModal = () => setIsModalOpenNew(false);

    const handleSave = async (event: Event) => {
        
        // normalization added
        const normalized = await plateApi.normalizeNumbers([event.registration_number]);
        if (normalized.length > 0) event.registration_number = normalized[0].plate_normalized;

        const isDuplicate = data.some(item => item.registration_number === event.registration_number);
        if (isDuplicate) {
            showInfo('Такий номер вже є у цьому списку');
            return;
        }
        event.entity_id = await reportApi.getAnyEntity({customerId: customerId || -1, type: 3});
        const newData = await reportApi.addEstimateVeh(event, customerId || -1);
        setData((prev) => [...prev, newData]);
        setIsModalOpenNew(false);
    }

    const handleDelete = async (row: EventRecord) => {
        const result = await showConfirm(t('common.delete_question', {id: row.registration_number}), t('common.delete_confirmation'));
        if (!result) return;

        const res = await reportApi.deleteEstimateVeh(customerId || -1, row.registration_number);
        if (!res) {
            showInfo('Delete error');
            return;
        }

        setData((prev) => prev.filter(p => p.registration_number !== row.registration_number));
    };

    return (
        <Box sx={{ width: '100%', p: 2 }}>
            {DialogComponent}

            <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                {/* Звіт про подію останнього прибуття */}
            </Typography>

            {/* Контейнер для панели инструментов */}
            <Box 
                sx={{ 
                    display: 'flex', 
                    gap: 2,             // Отступы между элементами
                    alignItems: 'center', // Выравнивание по вертикали (чтобы все были на одной линии)
                    mb: 3,              // Отступ снизу до таблицы
                    flexWrap: 'wrap'    // На случай узких экранов
                }}
            >
                {/* <LocalizedDatePicker
                    value={from}
                    onChange={(v) => setFrom(v)}
                    slotProps={{ textField: { size: 'small' } }} // Делаем высоту компактной
                    sx={{ width: 200 }}
                /> */}
                
                {/* <TextField
                    label="Період"
                    type="number"
                    size="small" // Задаем ту же высоту, что и у DatePicker
                    value={period_days}
                    onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        setPeriod_days(isNaN(val) ? 0 : val);
                    }}
                    sx={{ width: 120 }} // Ограничиваем ширину, чтобы он не растягивался
                /> */}

                <Button
                    variant="contained"
                    onClick={handleRefresh}
                    size="medium" // Чуть больше, чтобы идеально встать вровень с size="small" инпутов
                    sx={{ height: 40 }} // Либо явно фиксируем высоту кнопки под инпуты
                >
                    Оновити
                </Button>

                <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenNewModal()}
                    disabled={!!subtenantId}
                >
                    Додати нову подію
                </Button>
                
            </Box>
            
            <LocalizedGrid
                lang={i18n.language as any}
                gridId='LastInReportGrid'
                getRowId={(row: Event) => `${row.ts_start}_${row.registration_number}` }
                rows={data || []}
                columns={columns}
                pageSizeOptions={[10, 25, 100]}
                initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                }}
                disableRowSelectionOnClick
                autoHeight // Чтобы сетка не сжималась и подстраивалась под контент
                loading={loading}
            />

            <FormModal open={isModalOpen} onClose={handleCloseModal} ratio={2/3}>
                <EventForm
                    eventRecord={selectedEvent}
                    onCancel={handleCloseModal}
                />
            </FormModal>

            <FormModal open={isModalOpenNew} onClose={handleCloseNewModal} ratio={1/4}>
                <NewEventForm
                    entities={entities}
                    subtenants={subtenants}
                    onCancel={handleCloseNewModal}
                    onSave={handleSave}
                />
            </FormModal>

        </Box>
    )
}
